// src/components/editable/ImageThumbnail.tsx
// Shared clinical-image thumbnail component.
//
// Used by the Imaging EditableTable (one row == one synced disease image) AND by
// the Diagnosis detail modal's linked-image gallery (FullFieldModal), so it lives
// in its own module to keep a single source of truth and avoid a circular import
// between EditableTable and FullFieldModal.
//
// Clicking a real image invokes the `onOpen` callback (the caller owns the image
// SET, so it opens the in-app ImageLightbox carousel at the right index — see
// ImageLightbox.tsx). If no `onOpen` is provided the thumbnail is display-only.

import React from "react";
import { alpha, Box, Tooltip, useTheme } from "@mui/material";
import { Scanner } from "@mui/icons-material";
import { useTranslation } from "../../i18n";

// Small square image thumbnail for a clinical row. Renders the image when
// `imageUrl` resolves, else a subtle Scanner placeholder. Colors come from the
// theme; no hardcoded hex, no inline style.
export const ImageThumbnail: React.FC<{
  imageUrl?: string | null;
  size?: number;
  /** Invoked when the user clicks the image — the caller opens the lightbox. */
  onOpen?: () => void;
}> = ({ imageUrl, size = 40, onOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!imageUrl) {
    return (
      <Tooltip title={t("imaging.no_image", "No image")} arrow>
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            flexShrink: 0,
          }}
        >
          <Scanner sx={{ fontSize: size * 0.5, color: alpha(theme.palette.text.disabled, 0.6) }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("imaging.view_image", "View full image")} arrow>
      <Box
        component="img"
        src={imageUrl}
        alt={t("imaging.image_alt", "Imaging study")}
        loading="lazy"
        onClick={onOpen}
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          objectFit: "cover",
          cursor: onOpen ? "pointer" : "default",
          flexShrink: 0,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          transition: "border-color 0.15s ease, transform 0.15s ease",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            transform: "scale(1.04)",
          },
        }}
      />
    </Tooltip>
  );
};

export default ImageThumbnail;
